import { useState, useCallback } from "react";
import { useEffect } from "react";
import { API_URL } from "../config/API_URLS.tsx";
import { useLocation } from "react-router-dom";
import { authenticatedFetch } from "../auth/RequestSigner.tsx";
import { Link } from 'react-router-dom';

import {
  Card,
  Typography,
  CardBody,
  Chip,
  Avatar,
} from "@material-tailwind/react";

export function AprendizDataTab() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const documentByParams = queryParams.get("document");
  const [aprendizData, setAprendizData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLoad = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(
        `${API_URL}/v1/aprendiz-statu/${documentByParams}`,
        {
          method: "get",
          headers: {
            "Content-Type": "application/json"
          },
        }
      );
      if (!response.ok) {
        throw new Error("Error en la solicitud");
      }
      const data = await response.json();
      setAprendizData(data);
    } catch (error) {
      console.error("Error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [documentByParams]);

  useEffect(() => {
    if (!aprendizData) {
      handleLoad();
    }
  }, [handleLoad, aprendizData]);

  // Formatear fecha en formato DD/MM/YYYY
  // function formatDate(dateString) {
  //   const date = new Date(dateString);
  //   const day = date.getDate().toString().padStart(2, "0");
  //   const month = (date.getMonth() + 1).toString().padStart(2, "0");
  //   const year = date.getFullYear();
  //   return `${day}/${month}/${year}`;
  // }

  // Etiqueta de estado según state_id
  const getStatusLabel = (stateId) => {
    switch(stateId) {
      case 1:
        return "Pendiente";
      case 2:
        return "Aceptado";
      case 3:
        return "Rechazado";
      default:
        return "Desconocido";
    }
  };

  // Color de estado según state_id
  const getStatusColor = (stateId) => {
    switch(stateId) {
      case 1:
        return "orange";
      case 2:
        return "green";
      case 3:
        return "red";
      default:
        return "blue-gray";
    }
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-testid="aprendiz-data-tab">
      {loading && (
        <div className="flex items-center justify-center p-4" data-testid="aprendiz-data-loading">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-700"></div>
          <span className="ml-2">Cargando información del aprendiz...</span>
        </div>
      )}
      
      {!loading && aprendizData && (
        <Card className="h-full w-full shadow-md rounded-lg mt-6" data-testid="aprendiz-data-card">
          <CardBody className="overflow-x-auto px-0" data-testid="aprendiz-data-card-body">
            <table className="min-w-full divide-y divide-gray-300 rounded-lg overflow-hidden" data-testid="aprendiz-data-table">
              <thead className="bg-gray-50" data-testid="aprendiz-data-table-header">
                <tr>
                  <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="aprendiz-data-header-name">Nombre</th>
                  <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="aprendiz-data-header-document">Documento</th>
                  <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell" data-testid="aprendiz-data-header-ficha">Ficha</th>
                  <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="aprendiz-data-header-status">Estado</th>
                  <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="aprendiz-data-header-placa">Placa</th>
                  <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="aprendiz-data-header-actions">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-300" data-testid="aprendiz-data-table-body">
                <tr className="hover:bg-gray-50" data-testid="aprendiz-data-table-row">
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900" data-testid="aprendiz-data-name-cell">                   
                    <div className="flex items-center gap-2">
                      <Avatar src={aprendizData.photo} alt={"foto"} size="sm" data-testid="aprendiz-data-avatar" />
                      <div className="flex flex-col">
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal text-xs sm:text-sm"
                          data-testid="aprendiz-data-name-text"
                        >
                         {aprendizData.name} {aprendizData.last_name}
                        </Typography>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900" data-testid="aprendiz-data-document-cell">
                    <Typography
                      variant="small"
                      color=""
                      className="font-normal leading-none text-purple-700 text-xs sm:text-sm"
                    >
                      {aprendizData.document}
                    </Typography>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 hidden md:table-cell" data-testid="aprendiz-data-ficha-cell">
                    <Typography
                      variant="small"
                      color=""
                      className="font-normal leading-none text-purple-700 text-xs sm:text-sm"
                    >
                      {aprendizData.ficha}
                    </Typography>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap" data-testid="aprendiz-data-state-cell">
                    <Chip
                      variant="filled"
                      size="sm"
                      value={getStatusLabel(aprendizData.state_id)}
                      color={getStatusColor(aprendizData.state_id)}
                      data-testid="aprendiz-data-state-chip"
                    />
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900" data-testid="aprendiz-data-vehicle-placa-cell">
                    <Typography
                      variant="small"
                      color=""
                      className="font-normal leading-none text-purple-700 text-xs sm:text-sm"
                    >
                      {aprendizData.vehicle_0.placa || 'No disponible'}
                    </Typography>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900" data-testid="aprendiz-data-cell-actions">
                    <Link 
                      to={`/aprendiz-details/${aprendizData.document}`} 
                      className="text-amber-700 hover:text-amber-900 font-medium" 
                      data-testid="aprendiz-data-view-link"
                    >
                      Ver
                    </Link>
                  </td>
                </tr>
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      {!loading && error && (
        <div className="text-center text-red-600 p-4" data-testid="aprendiz-data-error">
          Error al cargar la información del aprendiz: {error}
        </div>
      )}
    </div>
  );
}
